import { useEffect, useState } from "react";
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import Swal from 'sweetalert2';
import { IUsuarioHistoricoStatus, IUsuarios } from "../../interfaces/IUsuarios";
import UsuariosService from "../../services/UsuariosService";

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

/*
 * DEVN3-780: ativar/inativar usuário pelo cadeado e histórico de status, compartilhado entre a
 * tela do Administrador (pages/usuariosAdmin) e a do Master (pages/usuarios). O acesso depende só
 * de "ativo" (docs/adr/0003-usuario-inativo-flag-unica.md).
 */

export const MOTIVO_TAMANHO_MAXIMO = 500;

export type FiltroStatusUsuario = 'ativos' | 'inativos' | 'todos';

export const usuarioEstaAtivo = (u: IUsuarios) => u.ativo !== false;

/** Confirma a ativação/inativação pedindo motivo opcional e chama a API. Devolve true se mudou. */
export async function alterarStatusUsuario(u: IUsuarios, t: (k: string) => string, aoConcluir: () => void): Promise<boolean> {
    const ativar = !usuarioEstaAtivo(u);
    const acao = ativar ? t('Ativar') : t('Inativar');

    const swal = Swal.mixin({
        customClass: { confirmButton: 'btn btn-modal-excluir ms-5', cancelButton: 'btn btn-modal-cancelar' },
        buttonsStyling: false
    });
    const result = await swal.fire({
        title: `${acao} ${t('Usuário')}`,
        text: ativar
            ? t('O usuário voltará a ter acesso à plataforma. Deseja ativá-lo?')
            : t('O usuário perderá o acesso à plataforma e será desconectado. Deseja inativá-lo?'),
        input: 'textarea',
        inputLabel: t('Motivo (opcional)'),
        inputAttributes: { maxlength: String(MOTIVO_TAMANHO_MAXIMO) },
        showCancelButton: true,
        cancelButtonText: t('Cancelar'),
        confirmButtonText: acao,
        reverseButtons: true
    });

    if (!result.isConfirmed) return false;

    return UsuariosService.alterarStatus(u.id, ativar, (result.value as string | undefined)?.trim(), aoConcluir);
}

/** Cadeado aberto = ativo (clique inativa); cadeado fechado = inativo (clique ativa). */
export function AcoesStatusUsuario({ item, onAlterarStatus, onHistorico }: {
    item: IUsuarios,
    onAlterarStatus: (u: IUsuarios) => void,
    onHistorico: (u: IUsuarios) => void
}) {
    const { t } = useTranslation();
    const ativo = usuarioEstaAtivo(item);
    return <>
        <i
            onClick={() => onAlterarStatus(item)}
            title={ativo ? t('Inativar') : t('Ativar')}
            className={ativo ? 'bi bi-unlock fs-2' : 'bi bi-lock-fill fs-2'}
            style={{ color: ativo ? '#5E6397' : '#f1416c', cursor: 'pointer' }}
        />
        <i
            onClick={() => onHistorico(item)}
            title={t('Histórico de ativação/inativação')}
            className='bi bi-clock-history fs-2'
            style={{ color: '#5E6397', cursor: 'pointer' }}
        />
    </>;
}

export function BadgeStatusUsuario({ item }: { item: IUsuarios }) {
    const { t } = useTranslation();
    const ativo = usuarioEstaAtivo(item);
    return <span className={`badge ${ativo ? 'badge-light-success' : 'badge-light-danger'}`}>
        {ativo ? t('Ativo') : t('Inativo')}
    </span>;
}

export function FiltroStatusUsuarioBotoes({ valor, onChange }: { valor: FiltroStatusUsuario, onChange: (f: FiltroStatusUsuario) => void }) {
    const { t } = useTranslation();
    const filtros: { label: string; value: FiltroStatusUsuario }[] = [
        { label: t('Ativos'), value: 'ativos' },
        { label: t('Inativos'), value: 'inativos' },
        { label: t('Todos'), value: 'todos' }
    ];
    return <div className="d-flex gap-2 align-items-center">
        {filtros.map(f => (
            <button
                key={f.value}
                type="button"
                onClick={() => onChange(f.value)}
                className={`btn btn-sm ${valor === f.value ? 'btn-primario' : 'btn-outline btn-outline-secondary'}`}
            >
                {f.label}
            </button>
        ))}
    </div>;
}

export function HistoricoStatusUsuarioModal({ usuario, onClose }: { usuario?: IUsuarios, onClose: () => void }) {
    const { t } = useTranslation();
    const [itens, setItens] = useState<IUsuarioHistoricoStatus[] | null>(null);

    useEffect(() => {
        setItens(null);
        if (usuario) {
            UsuariosService.historicoStatus(usuario.id).then(x => setItens(x ?? []));
        }
    }, [usuario]);

    return <Modal size='lg' centered={true} show={!!usuario} onHide={onClose}>
        <Modal.Header closeButton>
            <Modal.Title>{t('Histórico de ativação/inativação')} — {usuario?.nome}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            {itens === null ? <p>{t('Carregando...')}</p> : itens.length === 0 ? <p className="text-muted">{t('Sem registros')}</p> :
                <table className="table table-row-dashed align-middle">
                    <thead>
                        <tr className="fw-bold text-muted">
                            <th>{t('Data/hora')}</th>
                            <th>{t('Ação')}</th>
                            <th>{t('Quem fez')}</th>
                            <th>{t('Motivo')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {itens.map((h, i) => (
                            <tr key={i}>
                                <td style={{ whiteSpace: 'nowrap' }}>{moment.utc(h.data).local().format('DD/MM/YYYY HH:mm')}</td>
                                <td>
                                    <span className={`badge ${h.operacao === 'ATIVACAO' ? 'badge-light-success' : 'badge-light-danger'}`}>
                                        {h.operacao === 'ATIVACAO' ? t('Ativação') : t('Inativação')}
                                    </span>
                                </td>
                                <td>{h.realizadoPorNome ?? (h.realizadoPorID ? `#${h.realizadoPorID}` : t('Sistema'))}</td>
                                <td style={{ whiteSpace: 'pre-wrap' }}>{h.motivo || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            }
        </Modal.Body>
        <Modal.Footer>
            <button type="button" className="btn btn-modal-cancelar" onClick={onClose}>{t('Fechar')}</button>
        </Modal.Footer>
    </Modal>;
}
